/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/announcements.ts
 * 版本: 1.0.0
 * 创建日期: 2026-04-08
 * 创建人: AI Assistant
 *
 * 公告管理 API 封装服务
 *
 * 对应后端接口：/api/v1/announcements/
 * 序列化器：AnnouncementListSerializer / AnnouncementDetailSerializer / AnnouncementWriteSerializer
 *
 * 数据流：httpClient (baseURL=/api/v1/) + 相对路径 announcements/
 */

import { httpClient } from './http'

// ============================================================================
// 枚举与常量
// ============================================================================

/** 公告类型 */
export const AnnouncementType = {
  NORMAL: 'normal',
  IMPORTANT: 'important',
  EMERGENCY: 'emergency',
  MAINTENANCE: 'maintenance',
} as const
export type AnnouncementTypeValue = typeof AnnouncementType[keyof typeof AnnouncementType]

/** 公告类型配置（label + 颜色） */
export const ANNOUNCEMENT_TYPE_CONFIG: Record<AnnouncementTypeValue, { label: string; color: string; tagType: string }> = {
  normal: { label: '普通公告', color: '#409EFF', tagType: 'info' },
  important: { label: '重要通知', color: '#E6A23C', tagType: 'warning' },
  emergency: { label: '紧急公告', color: '#F56C6C', tagType: 'danger' },
  maintenance: { label: '系统维护', color: '#909399', tagType: '' },
}

/** 公告状态 */
export const AnnouncementStatus = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  PUBLISHED: 'published',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
} as const
export type AnnouncementStatusValue = typeof AnnouncementStatus[keyof typeof AnnouncementStatus]

/** 公告状态配置（label + 颜色） */
export const ANNOUNCEMENT_STATUS_CONFIG: Record<AnnouncementStatusValue, { label: string; color: string; tagType: string }> = {
  draft: { label: '草稿', color: '#909399', tagType: 'info' },
  scheduled: { label: '定时发布', color: '#E6A23C', tagType: 'warning' },
  published: { label: '已发布', color: '#67C23A', tagType: 'success' },
  expired: { label: '已过期', color: '#C0C4CC', tagType: 'info' },
  revoked: { label: '已撤回', color: '#F56C6C', tagType: 'danger' },
}

/** 目标受众 */
export const TargetAudience = {
  ALL: 'all',
  ENTERPRISE: 'enterprise',
  ENTERPRISE_ADMIN: 'enterprise_admin',
  EMPLOYEE: 'employee',
  MERCHANT: 'merchant',
  ORDER_BASED: 'order_based',
} as const
export type TargetAudienceValue = typeof TargetAudience[keyof typeof TargetAudience]

/** 目标受众配置 */
export const TARGET_AUDIENCE_CONFIG: Record<TargetAudienceValue, { label: string }> = {
  all: { label: '全部' },
  enterprise: { label: '企业端（企业管理员+员工）' },
  enterprise_admin: { label: '仅企业管理员' },
  employee: { label: '仅员工' },
  merchant: { label: '商户端' },
  order_based: { label: '已下单员工' },
}

// ============================================================================
// 类型定义
// ============================================================================

/** 关联实体简要信息 */
export interface RelatedEntity {
  id: string
  name: string
}

/** 公告列表项（轻量级） */
export interface AnnouncementListItem {
  id: string
  title: string
  summary_text: string
  announcement_type: AnnouncementTypeValue
  announcement_type_display: string
  target_audience: TargetAudienceValue
  target_audience_display: string
  status: AnnouncementStatusValue
  status_display: string
  is_popup: boolean
  is_pinned: boolean
  is_active: boolean
  publish_at: string | null
  expire_at: string | null
  actual_published_at: string | null
  delivered_count: number
  read_count: number
  created_by_name: string | null
  created_at: string
  updated_at: string
}

/** 订单过滤条件（target_audience=order_based 时使用） */
export interface OrderFilterConfig {
  service_date?: string
  consumption_type?: string
  meal_segment_code?: string
  restaurant_id?: number
}

/** 公告详情（完整） */
export interface AnnouncementDetail extends AnnouncementListItem {
  content: string
  summary: string
  target_enterprises_detail: RelatedEntity[]
  target_workplaces_detail: RelatedEntity[]
  target_merchants_detail: RelatedEntity[]
  order_filter_config: OrderFilterConfig | null
  source_enterprise: string | null
  source_enterprise_name: string | null
  sort_order: number
  created_by: string | null
  published_by: string | null
  published_by_name: string | null
  is_read: boolean
}

/** 公告弹窗项 */
export interface AnnouncementPopupItem {
  id: string
  title: string
  content: string
  summary_text: string
  announcement_type: AnnouncementTypeValue
  announcement_type_display: string
  is_popup: boolean
  actual_published_at: string | null
  created_at: string
}

/** 创建/编辑公告表单数据 */
export interface AnnouncementFormData {
  title: string
  content: string
  summary?: string
  announcement_type: AnnouncementTypeValue
  target_audience: TargetAudienceValue
  target_enterprise_ids?: number[]
  target_workplace_ids?: number[]
  target_merchant_ids?: number[]
  order_filter_config?: OrderFilterConfig | null
  publish_at?: string | null
  expire_at?: string | null
  is_popup?: boolean
  is_pinned?: boolean
  is_active?: boolean
  sort_order?: number
  source_enterprise?: string | null
}

/** 发布参数 */
export interface AnnouncementPublishParams {
  confirm?: boolean
}

/** 定时发布参数 */
export interface AnnouncementScheduleParams {
  publish_at: string
}

/** 公告列表查询参数 */
export interface AnnouncementListParams {
  page?: number
  page_size?: number
  status?: AnnouncementStatusValue
  announcement_type?: AnnouncementTypeValue
  target_audience?: TargetAudienceValue
  source_enterprise?: string | number
  search?: string
  ordering?: string
}

/** 分页响应 */
export interface PaginatedAnnouncementResponse {
  total: number
  page: number
  page_size: number
  results: AnnouncementListItem[]
}

/** 未读数响应 */
export interface UnreadCountResponse {
  unread_count: number
}

// ============================================================================
// API 服务
// ============================================================================

const BASE = 'announcements/'

/**
 * 公告管理 API 服务
 *
 * 运营端管理：list / create / detail / update / delete / publish / schedule / revoke
 * 消费端展示：active / popup / markRead / dismissPopup / unreadCount
 */
export const announcementsApi = {

  // ========================
  // 运营端管理接口
  // ========================

  /**
   * 获取公告列表（运营端，分页+筛选）
   *
   * 输入：AnnouncementListParams（分页/状态/类型/来源企业等）
   * 输出：PaginatedAnnouncementResponse
   */
  async list(params?: AnnouncementListParams): Promise<PaginatedAnnouncementResponse> {
    const response = await httpClient.get<PaginatedAnnouncementResponse>(
      BASE,
      { params }
    )
    return response
  },

  /**
   * 获取公告详情
   *
   * 输入：公告ID
   * 输出：AnnouncementDetail
   */
  async detail(id: string | number): Promise<AnnouncementDetail> {
    const response = await httpClient.get<AnnouncementDetail>(`${BASE}${id}/`)
    return response
  },

  /**
   * 创建公告（草稿）
   *
   * 输入：AnnouncementFormData
   * 输出：AnnouncementDetail（创建成功后返回完整详情）
   */
  async create(data: AnnouncementFormData): Promise<AnnouncementDetail> {
    const response = await httpClient.post<AnnouncementDetail>(BASE, data)
    return response
  },

  /**
   * 更新公告（仅 draft/revoked 状态可编辑）
   *
   * 输入：公告ID + 部分 AnnouncementFormData
   * 输出：AnnouncementDetail
   */
  async update(id: string | number, data: Partial<AnnouncementFormData>): Promise<AnnouncementDetail> {
    const response = await httpClient.patch<AnnouncementDetail>(`${BASE}${id}/`, data)
    return response
  },

  /**
   * 删除公告（仅 draft 状态可删除）
   *
   * 输入：公告ID
   * 输出：void
   */
  async remove(id: string | number): Promise<void> {
    await httpClient.delete(`${BASE}${id}/`)
  },

  /**
   * 立即发布公告 → 触发 Celery 异步投递
   *
   * 输入：公告ID + AnnouncementPublishParams
   * 输出：AnnouncementDetail
   */
  async publish(id: string | number, params?: AnnouncementPublishParams): Promise<AnnouncementDetail> {
    const response = await httpClient.post<AnnouncementDetail>(
      `${BASE}${id}/publish/`,
      params || { confirm: true }
    )
    return response
  },

  /**
   * 定时发布公告
   *
   * 输入：公告ID + { publish_at: ISO-8601 }
   * 输出：AnnouncementDetail
   */
  async schedule(id: string | number, params: AnnouncementScheduleParams): Promise<AnnouncementDetail> {
    const response = await httpClient.post<AnnouncementDetail>(
      `${BASE}${id}/schedule/`,
      params
    )
    return response
  },

  /**
   * 撤回已发布公告
   *
   * 输入：公告ID
   * 输出：AnnouncementDetail
   */
  async revoke(id: string | number): Promise<AnnouncementDetail> {
    const response = await httpClient.post<AnnouncementDetail>(`${BASE}${id}/revoke/`)
    return response
  },

  // ========================
  // 消费端接口
  // ========================

  /**
   * 获取当前用户可见的有效公告列表
   *
   * 输入：无
   * 输出：AnnouncementListItem[]
   */
  async active(): Promise<AnnouncementListItem[]> {
    const response = await httpClient.get<AnnouncementListItem[]>(`${BASE}active/`)
    return Array.isArray(response) ? response : []
  },

  /**
   * 获取需弹窗展示的公告（排除已关闭的）
   *
   * 输入：无
   * 输出：AnnouncementPopupItem[]
   */
  async popup(): Promise<AnnouncementPopupItem[]> {
    const response = await httpClient.get<AnnouncementPopupItem[]>(`${BASE}popup/`)
    return Array.isArray(response) ? response : []
  },

  /**
   * 标记公告已读
   *
   * 输入：公告ID
   * 输出：{ status: string }
   */
  async markRead(id: string | number): Promise<{ status: string }> {
    const response = await httpClient.post<{ status: string }>(`${BASE}${id}/mark-read/`)
    return response
  },

  /**
   * 关闭弹窗（不再弹出）
   *
   * 输入：公告ID
   * 输出：{ status: string }
   */
  async dismissPopup(id: string | number): Promise<{ status: string }> {
    const response = await httpClient.post<{ status: string }>(`${BASE}${id}/dismiss-popup/`)
    return response
  },

  /**
   * 获取未读公告数量
   *
   * 输入：无
   * 输出：UnreadCountResponse
   */
  async unreadCount(): Promise<UnreadCountResponse> {
    const response = await httpClient.get<UnreadCountResponse>(`${BASE}unread-count/`)
    return response || { unread_count: 0 }
  },
}
